/**
 * Proof page data: the proof view-model and the Re-run mutation.
 *
 * @module features/proofs/hooks/useProof
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { hasPendingBase } from '../evidenceKinds';
import type { ProofView } from '../types';

const proofKey = (proofId: string) => ['proof', proofId] as const;
const RERUN_POLL_MS = 5000;
/** Pending base runs are polled less often the longer they take: 5 s, then 15 s after 2 min, then 30 s after 10 min. */
const basePollMs = (waitedMs: number) => (waitedMs < 2 * 60_000 ? 5000 : waitedMs < 10 * 60_000 ? 15_000 : 30_000);
// One base-run attempt waits up to 30 min for a sandbox and 2 h for the run.
const BASE_RUN_DEADLINE_MS = 3 * 60 * 60 * 1000;

/** The proof; refetched every few seconds while a Re-run is in flight or a UI test's base run is still producing comparisons. */
export function useProof(proofId: string, isRerunning: boolean) {
  // When the current stretch of pending base runs began; polling for them stops after BASE_RUN_DEADLINE_MS.
  const pendingSince = React.useRef<number | null>(null);
  return useQuery({
    queryKey: proofKey(proofId),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/proofs/${proofId}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load the proof');
      return ((await res.json()) as { data: ProofView }).data;
    },
    refetchInterval: (query) => {
      if (!hasPendingBase(query.state.data?.evidence)) {
        pendingSince.current = null;
        return isRerunning ? RERUN_POLL_MS : false;
      }
      pendingSince.current ??= Date.now();
      const waited = Date.now() - pendingSince.current;
      if (isRerunning) return RERUN_POLL_MS;
      return waited < BASE_RUN_DEADLINE_MS ? basePollMs(waited) : false;
    },
  });
}

/** Starts the Re-run job (`POST /proofs/:proofId/rerun`); the caller polls the proof afterwards. */
export function useRerunProof(proofId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/proofs/${proofId}/rerun`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start the re-run');
    },
  });
}
